import { FC, JSX } from 'react'

import { RuleID } from '@forto-dev/insights-engine-models'
import { ChannelType, CorrelationType } from '@forto-dev/notification-center-models'
import { Separator, Stack } from '@forto-dev/phoenix-ui-core'
import { renderer } from '@forto-dev/renderer-phoenix-ui'

import { ActivityFeedItemProps } from '@/features/feed/view/types.ts'
import { CancelShipmentRequestButton, MOTUsefulnessSurvey, useIsSurveyEnabled } from '@/features/notifications'
import { useLocalisation } from '@/utils/localisation'

import { useRendererSDKContext } from '../hooks/useRendererSDKContext'

export const InsightActivityContent: FC<ActivityFeedItemProps> = ({ item, v2Flow }) => {
  const { currentLanguage } = useLocalisation()
  const notificationReference = item.activity.customField?.ruleID as RuleID // TODO: improve this type
  const isSurveyEnabled = useIsSurveyEnabled(notificationReference)
  const { rendererContext } = useRendererSDKContext({ item, channel: 'feed', renderType: 'activity' })

  if (!rendererContext) return null

  const isShipmentCreatedNotification = notificationReference === RuleID.MoT02_EV04_ShipmentCreated
  const shouldShowSurvey = isSurveyEnabled && notificationReference && !isShipmentCreatedNotification

  const feedContext = item.feedInfo.context

  // The cancel shipment request flow is only supported for v2 shipments, so hide the button for v1 shipments.
  const shouldShowCancelShipmentRequest = isShipmentCreatedNotification && v2Flow

  const injectedComponents = [
    shouldShowSurvey && notificationReference
      ? {
          nodeName: 'injectedSurvey',
          component: (
            <>
              <Separator orientation="horizontal" />
              <MOTUsefulnessSurvey
                reference={notificationReference}
                channelType={ChannelType.ActivityFeed}
                correlationID={item.id}
                correlationType={CorrelationType.ActivityFeed}
              />
            </>
          ),
        }
      : null,
    shouldShowCancelShipmentRequest
      ? {
          nodeName: 'cancelShipmentRequest',
          component: (
            <CancelShipmentRequestButton
              houseShipmentID={item.feedInfo.entityID}
              cargoReadyDate={feedContext?.cargoReadyDate}
              contactPerson={feedContext?.bookingInitiatorName}
              origin={feedContext?.origin}
              destination={feedContext?.destination}
              quoteCode={feedContext?.quoteCode ?? null}
            />
          ),
        }
      : null,
  ].filter(Boolean) as { nodeName: string; component: JSX.Element }[]

  return (
    <Stack gap={16}>
      {rendererContext &&
        renderer.render(
          rendererContext,
          'activity',
          currentLanguage,
          'someKey',
          injectedComponents.length > 0 ? injectedComponents : undefined
        )}
    </Stack>
  )
}
